const PLAIN_TEXT = /\S/;

export function assertMapWorkspaceExactObject(
  value: unknown,
  allowed: readonly string[],
  path: string,
): void {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    throw new Error(
      `Invalid map workspace descriptor: ${path} must be an object.`,
    );
  }
  const extras = Object.keys(value).filter((key) => !allowed.includes(key));
  if (extras.length > 0) {
    throw new Error(
      `Invalid map workspace descriptor: ${path} contains unsupported fields: ${extras.join(", ")}.`,
    );
  }
}

export function requireMapWorkspaceFields(
  value: unknown,
  required: readonly string[],
  path: string,
): void {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    throw new Error(
      `Invalid map workspace descriptor: ${path} must be an object.`,
    );
  }
  const missing = required.filter(
    (key) => !Object.prototype.hasOwnProperty.call(value, key),
  );
  if (missing.length > 0) {
    throw new Error(
      `Invalid map workspace descriptor: ${path} is missing required fields: ${missing.join(", ")}.`,
    );
  }
}

export function requireMapWorkspaceText(
  value: unknown,
  path: string,
): asserts value is string {
  if (
    typeof value !== "string" ||
    !PLAIN_TEXT.test(value) ||
    /[<>]/.test(value)
  ) {
    throw new Error(
      `Invalid map workspace descriptor: ${path} must be non-empty plain text.`,
    );
  }
}

export function uniqueMapWorkspaceIds(
  items: readonly { id: string }[],
  path: string,
): Set<string> {
  const ids = new Set<string>();
  for (const item of items) {
    requireMapWorkspaceText(item.id, `${path}.id`);
    if (ids.has(item.id))
      throw new Error(
        `Invalid map workspace descriptor: duplicate ${path} id "${item.id}".`,
      );
    ids.add(item.id);
  }
  return ids;
}

export function requireMapWorkspaceTextArray(
  value: readonly string[],
  path: string,
): void {
  if (!Array.isArray(value) || value.length === 0) {
    throw new Error(
      `Invalid map workspace descriptor: ${path} must contain non-empty plain text.`,
    );
  }
  value.forEach((item, index) =>
    requireMapWorkspaceText(item, `${path}[${index}]`),
  );
  if (new Set(value).size !== value.length) {
    throw new Error(
      `Invalid map workspace descriptor: ${path} must not contain duplicates.`,
    );
  }
}

export function requireMapWorkspaceEnum(
  value: unknown,
  allowed: readonly string[],
  path: string,
): void {
  if (typeof value !== "string" || !allowed.includes(value)) {
    throw new Error(
      `Invalid map workspace descriptor: ${path} "${String(value)}" is unsupported.`,
    );
  }
}

export function validateMapWorkspaceAvailabilityShape(
  value: unknown,
  path: string,
): void {
  const status = value && typeof value === "object" && "status" in value
    ? value.status
    : null;
  if (status === "available") {
    assertMapWorkspaceExactObject(value, ["status", "value"], path);
    requireMapWorkspaceFields(value, ["status", "value"], path);
    return;
  }
  if (status === "unavailable") {
    assertMapWorkspaceExactObject(value, ["status", "reason"], path);
    requireMapWorkspaceFields(value, ["status", "reason"], path);
    return;
  }
  throw new Error(
    `Invalid map workspace descriptor: ${path}.status must be available or unavailable.`,
  );
}
